import React from 'react'
import { Icon, type TIconProps } from '~/components/icon'
import * as Comp from './checkbox.styles'
import { CheckboxLabel } from './components/checkbox-label'
import { CheckboxHelperText } from './components/helper-text'
import { useCheckboxVariants } from './hooks'
import type { TCheckboxProps } from './types'

const CheckboxRoot = ({
  size = 'md',
  checked = false,
  indeterminate = false,
  onPress,
  error,
  disabled,
  readOnly,
  children,
  testID,
}: TCheckboxProps) => {
  const { iconSizeMapping, renderCurrentStateCheckbox, pressed, setPressed } =
    useCheckboxVariants()

  const renderInternalLabel = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return

    if (React.isValidElement(child) && child.type === CheckboxLabel) {
      return React.cloneElement(child as React.ReactElement<TCheckboxProps>, {
        size,
        disabled,
        readOnly,
        error,
      })
    }
  })

  const renderInternalHelperText = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return

    if (React.isValidElement(child) && child.type === CheckboxHelperText) {
      return React.cloneElement(child as React.ReactElement<TCheckboxProps>, {
        size,
      })
    }
  })

  const renderNestedParent = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return

    if (child.type === Comp.NestedContainer) {
      return React.cloneElement(child as React.ReactElement<TCheckboxProps>, {
        testID: 'checkbox-nested-container',
      })
    }
  })

  const componentProperties = {
    size,
    hasPressed: pressed,
    hasDisabled: disabled,
    hasError: !!error,
    hasReadOnly: readOnly,
    hasInternalLabel: !!renderInternalLabel?.length,
    readOnly,
  }
  const iconColorMapping = (): TIconProps['color'] => {
    return disabled
      ? 'colorTextNeutralDisabled'
      : readOnly
        ? 'colorTextNeutralReadonly'
        : 'colorTextNeutralDefault'
  }
  return (
    <Comp.Root
      testID={testID}
      disabled={disabled || readOnly}
      onPress={() => {
        if (typeof onPress === 'function') {
          onPress()
        }
      }}
      onPressIn={() => setPressed(true)}
      onPressOut={() => setPressed(false)}
      {...componentProperties}
    >
      <Comp.Actions {...componentProperties}>
        <Icon
          testID="checkbox-icon"
          color={iconColorMapping()}
          size={iconSizeMapping[size]}
          symbol={renderCurrentStateCheckbox(indeterminate, checked)}
        />
        {renderInternalLabel}
      </Comp.Actions>
      {error && !!renderInternalLabel?.length ? (
        <Comp.HelperTextContainer {...componentProperties}>
          {renderInternalHelperText}
        </Comp.HelperTextContainer>
      ) : null}
      {renderNestedParent}
    </Comp.Root>
  )
}

export const Checkbox = {
  Root: CheckboxRoot,
  Label: CheckboxLabel,
  HelperText: CheckboxHelperText,
  NestedContainer: Comp.NestedContainer,
}

export type { TCheckboxProps }
